import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, MoreHorizontal, Edit, Trash2, RefreshCw } from "lucide-react";
import { useFetchAppTags, useToggleAppTagStatus, AppTag } from "@/hooks/useAppTags";
import { CreateTagModal } from "@/components/tags/CreateTagModal";
import { EditTagModal } from "@/components/tags/EditTagModal";
import { DeleteTagDialog } from "@/components/tags/DeleteTagDialog";

export default function ReportsTags() {
  const [searchTerm, setSearchTerm] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<AppTag | null>(null);

  const { data: tags, isLoading, error, refetch } = useFetchAppTags();
  const toggleStatusMutation = useToggleAppTagStatus();

  // Filter tags by search term
  const filteredTags = useMemo(() => {
    if (!tags) return [];
    if (!searchTerm.trim()) return tags;

    const search = searchTerm.toLowerCase();
    return tags.filter(
      (tag) =>
        tag.tag_name.toLowerCase().includes(search) ||
        tag.tag_description.toLowerCase().includes(search)
    );
  }, [tags, searchTerm]);

  const handleEdit = (tag: AppTag) => {
    setSelectedTag(tag);
    setEditModalOpen(true);
  };

  const handleDelete = (tag: AppTag) => {
    setSelectedTag(tag);
    setDeleteDialogOpen(true);
  };

  const handleToggleStatus = async (tag: AppTag) => {
    try {
      await toggleStatusMutation.mutateAsync({
        id: tag.id,
        is_active: !tag.is_active,
      });
    } catch (error) {
      // Error handled by mutation
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <p className="text-red-600">Error loading tags: {error.message}</p>
        <Button onClick={() => refetch()} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-[1400px]">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Tags</h1>
          <p className="text-gray-600 mt-1">Manage your tags</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => refetch()} variant="outline" size="icon">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={() => setCreateModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Tag
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            type="text"
            placeholder="Search tags by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-gray-400" />
            <p className="text-gray-600">Loading tags...</p>
          </div>
        </div>
      ) : filteredTags.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 border border-gray-200 rounded-lg">
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-2">
              {searchTerm ? "No tags found" : "No tags yet"}
            </h3>
            <p className="text-gray-600 mb-4">
              {searchTerm
                ? "Try adjusting your search term"
                : "Create your first tag to get started"}
            </p>
            {!searchTerm && (
              <Button onClick={() => setCreateModalOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Tag
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tag Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-24">Version</TableHead>
                <TableHead className="w-24">Status</TableHead>
                <TableHead className="w-48">Created</TableHead>
                <TableHead className="w-20 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTags.map((tag) => (
                <TableRow key={tag.id}>
                  <TableCell className="font-medium">{tag.tag_name}</TableCell>
                  <TableCell className="max-w-md">
                    <span className="text-gray-600">
                      {truncateText(tag.tag_description, 100)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">v{tag.version_number}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={tag.is_active ? "default" : "outline"}
                      className={
                        tag.is_active
                          ? "bg-green-100 text-green-800 hover:bg-green-200"
                          : "bg-gray-100 text-gray-600"
                      }
                    >
                      {tag.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {formatDate(tag.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(tag)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleStatus(tag)}>
                          {tag.is_active ? "Deactivate" : "Activate"}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDelete(tag)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Showing count */}
      {!isLoading && filteredTags.length > 0 && (
        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredTags.length} of {tags?.length || 0} tag
          {(tags?.length || 0) !== 1 ? "s" : ""}
        </div>
      )}

      {/* Modals */}
      <CreateTagModal open={createModalOpen} onOpenChange={setCreateModalOpen} />
      <EditTagModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        tag={selectedTag}
      />
      <DeleteTagDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        tag={selectedTag}
      />
    </div>
  );
}
